import { Box, Button, Checkbox, Skeleton, Typography } from "@mui/material"
import { Building2, UserPlus, Bot, User, Mail, ShieldCheck, Plus, AlertTriangle } from "lucide-react"

import type { EmailRecipient } from "../email-target-selector"
import { ConfidenceChip, typeRowSx } from "./chips"

/**
 * The per-type recipient list (one tab of the email target selector's "browse"
 * view), extracted from `email-target-selector.tsx`. Loading skeletons, an empty
 * state, the select-all / select-verified controls, and the scrollable row list.
 * No money / reseller here — wall-neutral.
 */
export interface RecipientListProps {
  contacts: EmailRecipient[]
  onContactToggle: (contact: EmailRecipient, checked: boolean) => void
  recipients: EmailRecipient[]
  isLoading: boolean
  type: 'customer' | 'prospect' | 'manual' | 'contact' | 'ai_prospect'
  onSelectAll: () => void
  /** AI prospects only: add only the verified/deliverable addresses. */
  onSelectAllVerified?: () => void
  verifiedCount?: number
  /** Render a per-row Verified/Unverified confidence chip (AI prospects). */
  showConfidence?: boolean
  /** Friendlier empty-state line than the generic "adjust your search". */
  emptyHint?: string
  hasMore?: boolean
  loadedCount?: number
  totalCount?: number
  loadingMore?: boolean
  onLoadMore?: () => void
}

/** Human label for a recipient type (used in empty-state copy). */
function typeLabelPlural(type: string): string {
  return type === 'ai_prospect' ? 'AI prospects' : `${type}s`
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'customer':
      return <Box sx={{ color: "primary.main", display: "flex" }}><Building2 size={16} /></Box>
    case 'prospect':
      return <Box sx={{ color: "success.main", display: "flex" }}><UserPlus size={16} /></Box>
    case 'ai_prospect':
      return <Box sx={{ color: "info.main", display: "flex" }}><Bot size={16} /></Box>
    case 'contact':
      return <Box sx={{ color: "secondary.main", display: "flex" }}><User size={16} /></Box>
    case 'manual':
      return <Box sx={{ color: "text.secondary", display: "flex" }}><Mail size={16} /></Box>
    default:
      return <Box sx={{ color: "text.secondary", display: "flex" }}><Mail size={16} /></Box>
  }
}

export function RecipientList({ contacts, onContactToggle, recipients, isLoading, type, onSelectAll, onSelectAllVerified, verifiedCount, showConfidence, emptyHint, hasMore, loadedCount, totalCount, loadingMore, onLoadMore }: RecipientListProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Skeleton variant="rounded" sx={{ width: 16, height: 16 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="rounded" sx={{ width: 128, height: 16, mb: 0.5 }} />
              <Skeleton variant="rounded" sx={{ width: 192, height: 12 }} />
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  if (contacts.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
        <Box sx={{ mx: "auto", width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, border: 1, ...typeRowSx(type) }}>
          {getTypeIcon(type)}
        </Box>
        <Typography sx={{ fontSize: 14 }}>No {typeLabelPlural(type)} found</Typography>
        <Typography sx={{ fontSize: 12, mt: 0.5 }}>{emptyHint || 'Try adjusting your search criteria'}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Select-all controls. AI prospects get a primary "verified-only" action
          (the safe default) plus a secondary "all" that includes unverified. */}
      {contacts.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pb: 1, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {onSelectAllVerified && (
              <Button
                type="button"
                variant="contained"
                color="success"
                size="small"
                disableElevation
                disabled={!verifiedCount}
                onClick={onSelectAllVerified}
                startIcon={<ShieldCheck size={12} />}
                sx={{ fontSize: 12 }}
              >
                Select verified ({verifiedCount ?? 0})
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={onSelectAll}
              startIcon={<Plus size={12} />}
              sx={{ fontSize: 12 }}
            >
              Select all ({contacts.length})
            </Button>
          </Box>
          {showConfidence && (
            <Typography sx={{ fontSize: 11, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
              <AlertTriangle size={11} />
              Unverified emails were guessed by the agent and may bounce — pick them deliberately.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ height: 256, overflow: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {contacts.map((contact) => {
            const isSelected = recipients.some(r => r.id === contact.id)

            return (
              <Box
                key={contact.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.75,
                  px: 1.25,
                  border: 1,
                  borderRadius: 1,
                  transition: "background-color .15s",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  ...(isSelected ? typeRowSx(contact.type) : { bgcolor: "background.paper", borderColor: "divider" }),
                }}
                onClick={() => onContactToggle(contact, !isSelected)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => {}} // Handled by parent onClick
                  size="small"
                  sx={{ flexShrink: 0, p: 0 }}
                />

                {/* Contact Avatar */}
                <Box sx={{ flexShrink: 0, width: 26, height: 26, bgcolor: "surfaceSoft", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary" }}>
                    {contact.name ? contact.name.charAt(0).toUpperCase() : contact.email ? contact.email.charAt(0).toUpperCase() : '?'}
                  </Typography>
                </Box>

                {/* Contact Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {contact.name || contact.email || 'Unknown Contact'}
                    </Typography>
                    {getTypeIcon(contact.type)}
                    {showConfidence && <ConfidenceChip confidence={contact.email_confidence} />}
                  </Box>
                  <Box sx={{ fontSize: 11.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>
                    <Box component="span" sx={{ mr: 0.5, display: "inline-flex" }}><Mail size={11} /></Box>
                    {contact.email}
                    {contact.company && <Box component="span" sx={{ mx: 0.5, color: "text.disabled" }}>·</Box>}
                    {contact.company && <Box component="span" sx={{ color: "text.disabled", overflow: "hidden", textOverflow: "ellipsis" }}>{contact.company}</Box>}
                  </Box>
                </Box>
              </Box>
            )
          })}
          {hasMore && onLoadMore && (
            <Button
              variant="text"
              color="inherit"
              size="small"
              fullWidth
              onClick={onLoadMore}
              disabled={!!loadingMore}
              sx={{ fontSize: 12, color: "text.secondary" }}
            >
              {loadingMore ? 'Loading...' : `Load more (${loadedCount} of ${totalCount})`}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}
